import {
    HDate, Sedra, HebrewDateEvent, gematriya, HebrewCalendar,
    Location, Event, flags
} from '@hebcal/core';
import { getPirkeiAvotPerek } from './pirkeiAvot';

// Your event flags for extra designations on the Hebrew date.
const hebrewDateFlags = [
    { flag: flags.CHANUKAH_CANDLES, text: 'נרות חנוכה' },
    { flag: flags.CHOL_HAMOED, text: 'חול המועד' },
    { flag: flags.EREV, text: 'ערב' },
    { flag: flags.MAJOR_FAST, text: 'צום גדול' },
    { flag: flags.MINOR_FAST, text: 'צום קטן' },
    { flag: flags.MINOR_HOLIDAY, text: 'חג מינורי' },
    { flag: flags.OMER_COUNT, text: 'ספירת העומר' },
    { flag: flags.ROSH_CHODESH, text: 'ראש חודש' },
    { flag: flags.SPECIAL_SHABBAT, text: 'שבת מיוחדת' }
];

// Enum for special day types.
export const SpecialDayTypeEnum = {
    PURIM: 'Purim',
    YOM_KIPPUR: 'YomKippur',
    CHANUKAH: 'Chanukah',
    PESACH: 'Pesach',
    SUKKOT: 'Sukkot',
    SHAVUOT: 'Shavuot',
    ROSH_HASHANAH: 'RoshHashanah',
    // Optionally, include an entry for Rosh Chodesh.
    ROSH_CHODESH: 'RoshChodesh',
    TAANIS_ESTHER: 'TAANIS_ESTHER',
    TISHA_BAV: 'TishaBav' // Add Tisha B'Av
};

const latitude = 41.189120;
const longitude = -74.055079;
const elevation = 167.67;
const tzid = 'America/New_York';
const location = new Location(latitude, longitude, false, tzid, 'Pomona NY', 'US', null, elevation);

const options = {
    isHebrewYear: true,
    candlelighting: false,
    location: location,
    sedrot: true,
    omer: true,
};

/**
 * Strips Hebrew nikkud (vowel marks) and cantillation marks from Hebrew text
 * @param {string} text - Hebrew text with nikkud
 * @returns {string} Hebrew text without nikkud
 */
function stripNikkud(text) {
    if (!text) return text;
    // Remove Hebrew vowel marks (nikkud) and cantillation marks
    // Exclude Hebrew maqaf (U+05BE) which is the dash used to connect Hebrew words
    return text.replace(/[\u0591-\u05BD\u05BF-\u05C7]/g, '');
}

/**
 * Returns the weekly portion string (parsha) for the week of the given date.
 * This includes handling extra parshios (like special Shabbat events).
 *
 * @param {Date|string} date - The date to compute the parsha for.
 * @param {HDate} hDate - The HDate instance corresponding to the date.
 * @returns {string} The weekly portion string in Hebrew without nikkud.
 */
export function getWeeklyPortionForDate(date, hDate) {
    console.log(date, hDate);
    if (!hDate)
        hDate = new HDate(date);
    options.start = hDate;
    options.end = hDate;

    let weeklyPortion;
    const events = HebrewCalendar.calendar(options);
    // Check for an extra parsha in a special event (e.g. SPECIAL_SHABBAT).
    let specialParsha = null;
    for (const ev of events) {
        console.log('parsha event', ev);
        if ((ev.mask & flags.SPECIAL_SHABBAT) !== 0) {
            // Remove the "שבת " prefix and strip nikkud.
            specialParsha = stripNikkud(ev.render('he').replace('שַׁבָּת ', ''));
        }
        else if ((ev.mask & flags.PARSHA_HASHAVUA) !== 0) {
            weeklyPortion = stripNikkud(ev.render('he'));
        } else {
            //console.error('NOT MATCHED EVENT', ev);
        }
    }

    // Update weeklyPortion based on additional event flags from hDate.events.
    // (Here we assume hDate.events is an array for some flags; adjust if it's a bit mask.)
    if (hDate.events && hDate.events.includes(Event.LIGHT_CANDLES)) {
        if (!weeklyPortion.includes("שבת")) {
            weeklyPortion = `שבת, ${weeklyPortion}`;
        }
    }
    if (hDate.events && hDate.events.includes(Event.PARSHA_HASHAVUA)) {
        weeklyPortion += ' (פרשת השבוע)';
    }
    if (hDate.events && hDate.events.includes(Event.SHABBAT_MEVARCHIM)) {
        weeklyPortion += ', שבת מעביר';
    }

    if (specialParsha) {
        weeklyPortion += ' - ' + specialParsha;
    }

    return weeklyPortion;
}

/**
 * Determines if the given date is a special day (e.g. Purim, Yom Kippur, Chanukah, etc.)
 * and returns an object with a Hebrew title and a specialDayType.
 *
 * @param {Date|string} date - The date to check.
 * @param {HebrewDateEvent} hebrewDateEvent - The rendered Hebrew date event.
 * @returns {object|null} An object with { title, specialDayType } if a special day is found; otherwise null.
 */
export function getSpecialDayForDate(date, hebrewDateEvent) {
    let hDate = new HDate(date);

    options.start = hDate;
    options.end = hDate;

    const events = HebrewCalendar.calendar(options);
    let specialDay = null;

    for (const ev of events) {
        // Use ev.desc to determine the holiday, converting to lowercase.
        const evDescLower = ev.desc?.toLowerCase();
        //console.log(date, evDescLower);
        // First day Yom Tov special handling
        if (evDescLower === 'pesach' || evDescLower?.startsWith('pesach')) {
            specialDay = { title: 'פסח', specialDayType: SpecialDayTypeEnum.PESACH };
            break;
        } else if (evDescLower === 'sukkot' || evDescLower?.startsWith('sukkot')) {
            specialDay = { title: 'סוכות', specialDayType: SpecialDayTypeEnum.SUKKOT };
            break;
        } else if (evDescLower === 'shavuot' || evDescLower?.startsWith('shavuot')) {
            specialDay = { title: 'שבועות', specialDayType: SpecialDayTypeEnum.SHAVUOT };
            break;
        } else if (evDescLower?.includes('rosh hashana') || evDescLower?.includes('rosh-hashanah')) {
            specialDay = { title: 'ראש השנה', specialDayType: SpecialDayTypeEnum.ROSH_HASHANAH };
            break;
        } else if (evDescLower === 'shmini atzeret' || evDescLower?.includes('shmini atzeret') || evDescLower?.includes('simchat torah')) {
            specialDay = { title: 'שמיני עצרת', specialDayType: SpecialDayTypeEnum.SUKKOT };
            break;
        }
        
        // Other special days
        switch (evDescLower) {
            case 'purim':
                specialDay = { title: 'פורים', specialDayType: SpecialDayTypeEnum.PURIM };
                break;
            case 'yom-kippur':
            case 'yom kippur':
                specialDay = { title: 'יום כיפור', specialDayType: SpecialDayTypeEnum.YOM_KIPPUR };
                break;
            case 'chanukah':
                specialDay = { title: 'חנוכה', specialDayType: SpecialDayTypeEnum.CHANUKAH };
                break;
            case 'pesach':
                specialDay = { title: 'פסח', specialDayType: SpecialDayTypeEnum.PESACH };
                break;
            case 'sukkot':
                specialDay = { title: 'סוכות', specialDayType: SpecialDayTypeEnum.SUKKOT };
                break;
            case 'shavuot':
                specialDay = { title: 'שבועות', specialDayType: SpecialDayTypeEnum.SHAVUOT };
                break;
            case 'rosh-hashanah':
            case 'rosh hashanah':
                specialDay = { title: 'ראש השנה', specialDayType: SpecialDayTypeEnum.ROSH_HASHANAH };
                break;
            case 'ta\'anit esther':
                specialDay = { title: 'תענית אסתר', specialDayType: SpecialDayTypeEnum.TAANIS_ESTHER };
                break;
            case 'tish\'a b\'av':
            case 'tisha b\'av':
                specialDay = { title: 'תשעה באב', specialDayType: SpecialDayTypeEnum.TISHA_BAV };
                break;
            default:
                // If no direct match, check event flags.
                if ((ev.mask & flags.ROSH_CHODESH) !== 0) {
                    // For Rosh Chodesh, include the month name from the Hebrew date.

                    if (hDate.getDate() === 30) {
                        hDate = hDate.next();
                    }

                    specialDay = {
                        title: 'ראש חודש',
                        specialDayType: SpecialDayTypeEnum.ROSH_CHODESH,
                        month: hDate.getMonthName()
                    };
                }
                break;
        }
        if (specialDay) break;
    }
    return specialDay;
}

/**
 * Main function that loads core Hebrew calendar data for a given date.
 * It returns the Hebrew date string, weekly portion (parsha), Hebrew day number,
 * and a special day object if applicable.
 *
 * @param {Date|string} date - The date to load data for.
 * @returns {object} An object with { hebrewDate, weeklyPortion, hebrewDay, specialDay }.
 */
export function loadCoreData(date) {
    const hDate = new HDate(date);
    const hebrewDateEvent = new HebrewDateEvent(hDate);
    let hebrewDate = hebrewDateEvent.renderBrief('he');

    // Set up location and calendar options.


    // Get the weekly portion string (parsha) for the week.
    const weeklyPortion = getWeeklyPortionForDate(date, hDate);

    // Determine if today is a special day.
    const specialDay = getSpecialDayForDate(date, hebrewDateEvent);

    // Optionally, append the special day title to the Hebrew date.
    if (specialDay) {
        hebrewDate += ' - ' + specialDay.title;
    }

    // Determine the Hebrew day number via gematriya.
    const hebrewDay = gematriya(hDate.dd);

    return { hebrewDate, weeklyPortion, hebrewDay, specialDay };
}
